import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import {
    GENERAL_ENTRY_EXIT_ITEM_MODEL,
    MOVEMENT_DIRECTION_SECTION_DEFAULTS,
    buildEntryAndExitFormItem
} from "../../Model/Model";
import {GetMeasurementUnits} from "../../../../../reduxToolkit/FoodieIngredient/IngredientApi";
import HandleChange from "../../../../../Helpers/HandleChange";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 */
const GeneralExitSectionsApp = ({
    materialRequest,
    setMaterialRequest,
    setEmptyFieldsItems
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { measurementUnitsAll } = useSelector((state) => state.IngredientSlice);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedLevelId, setSelectedLevelId] = useState(null);
    const { handleChange } = HandleChange({ model: materialRequest, setModel: setMaterialRequest });

    /**
     * Handles changes for dynamically created material request items.
     *
     * Updates the corresponding item field value based on the provided index.
     *
     * @param {Object} e - Input change event.
     * @param {number} index - Index of the item being modified.
     *
     * @returns {void}
     */
    const handleChangeTemp = (e, index) => {
        const { name, value } = e.target;

        setMaterialRequest((prev) => {
            const items = [...prev.items];

            items[index] = {
                ...items[index],
                [name]: value,
            };

            return {
                ...prev,
                items,
            };
        });
    };

    /**
     * Sets the movement direction of the form.
     *
     * The selected direction decides which sections are enabled:
     * - ENTRY enables the departure and entry sections.
     * - EXIT enables the arrival and exit sections.
     *
     * When the direction changes, every field enabled by the previous direction
     * is restored to its default value, so the information captured for it is
     * neither displayed nor submitted.
     *
     * @param {number} movementDirection - Selected movement direction.
     *
     * @returns {void}
     */
    const handleSelectMovementDirection = (movementDirection) => {
        if (materialRequest?.movementDirection === movementDirection)
            return;

        setEmptyFieldsItems(false);
        setMaterialRequest((prev) => ({
            ...prev,
            ...MOVEMENT_DIRECTION_SECTION_DEFAULTS,
            expectedDateAndTime: null,
            items: [buildEntryAndExitFormItem(GENERAL_ENTRY_EXIT_ITEM_MODEL)],
            movementDirection,
        }));
    };

    /**
     * Loads measurement units on component initialization.
     * The measurement units are used to populate item unit selectors
     * within the material request form.
     */
    useEffect(() => {
        dispatch(GetMeasurementUnits());
    }, [dispatch]);

    /**
     * Adds a new empty item to the material request.
     *
     * Before adding a new item, validates that all existing items
     * contain the required information.
     *
     * If validation fails:
     * - Displays item validation errors.
     *
     * If validation succeeds:
     * - Appends a new item initialized from GENERAL_ENTRY_EXIT_ITEM_MODEL.
     *
     * @returns {void}
     */
    const handleAdd = () => {
        if(!materialRequest?.isValidItems()){
            setEmptyFieldsItems(true);
            return;
        }
        setEmptyFieldsItems(false);
        setMaterialRequest(prev => ({
            ...prev,
            items: [...prev.items, buildEntryAndExitFormItem(GENERAL_ENTRY_EXIT_ITEM_MODEL)]
        }));
    };

    /**
     * Removes an item from the material request item list.
     *
     * Displays a success notification after deletion.
     *
     * @param {Object} item - Item to remove from the request.
     *
     * @returns {void}
     */
    const handleDeleteItem = (item) => {
        setMaterialRequest((prev) => ({
            ...prev,
            items: prev.items.filter((currentItem) => currentItem.id !== item.id),
        }));
        toast.success(t('has_been_deleted_successfully'));
    };

    return {
        t,
        handleChange,
        handleChangeTemp,
        handleSelectMovementDirection,
        showDeleteModal,
        setShowDeleteModal,
        selectedLevelId,
        setSelectedLevelId,
        measurementUnitsAll,
        handleAdd,
        handleDeleteItem,
    };
};

export default GeneralExitSectionsApp;
